import { Asset } from 'types'

import PDSAssetTag from 'designSystem/components/PDSAssetTag/PDSAssetTag'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSLinkWrapper from 'designSystem/components/PDSLinkWrapper.tsx/PDSLinkWrapper'
import PDSSubCard from 'designSystem/components/PDSSubCard/PDSSubCard'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSTooltip from 'designSystem/components/PDSTooltip/PDSTooltip'
import PDSValueTag from 'designSystem/components/PDSValueTag/PDSValueTag'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { formatAsCurrency } from 'utils'
import { roundToHundredths } from 'utils/numberUtils'

import ProxyETFTooltipContent from './TooltipContent/ProxyETFTooltipContent'

type Props = {
  asset: Asset
  portfolioId: string
}

export default function PortfolioAssetListItem({ asset, portfolioId }: Props) {
  const {
    averageCost,
    alternativeAssetSymbol,
    name,
    symbol,
    price,
    priceDetails,
    shares,
    totalValue,
    percentageOfPortfolio,
    isSupported,
  } = asset

  const dayGain = priceDetails?.dayGain

  const gainLoss = price ? price - averageCost : Infinity

  let bordercolor = isSupported ? COLOR.neutral300 : COLOR.alert400
  let textColor = isSupported ? COLOR.textSecondary : COLOR.alert400

  return (
    <PDSLinkWrapper
      testId={`asset-item-${symbol.toLowerCase()}`}
      to={`/asset/${symbol}?portfolioId=${portfolioId}`}
    >
      <PDSSubCard
        alignItems="center"
        borderColor={bordercolor}
        className={`interactive-card ${isSupported ? '' : 'alert'}`}
        display="flex"
        gap={SIZE.s8}
        justifyContent="space-between"
        paddingSize="md"
      >
        <PDSFlexBox alignItems="center" width="26%">
          <PDSAssetTag
            marginRight={SIZE.s8}
            symbol={symbol}
            variant={isSupported ? 'neutral' : 'alert'}
          />
          <PDSText color={textColor} hideOverflow={true} weight="medium">
            {name}
          </PDSText>
          <PDSBox width="2.5rem" marginLeft={SIZE.s8}>
            {alternativeAssetSymbol ? (
              <PDSTooltip content={<ProxyETFTooltipContent />} />
            ) : null}
          </PDSBox>
        </PDSFlexBox>

        <PDSFlexBox gap={SIZE.s8} justifyContent="space-between" width="72%">
          <PDSBox flexBasis="5rem">
            <PDSText color={textColor} weight="medium">
              {isFinite(price) ? formatAsCurrency(price) : '-'}
            </PDSText>
          </PDSBox>

          <PDSBox flexBasis="4.5rem">
            <PDSText color={textColor} weight="medium">
              {roundToHundredths(shares)}
            </PDSText>
          </PDSBox>

          <PDSBox flexBasis="6rem">
            <PDSText color={textColor} weight="medium">
              {isFinite(totalValue) ? formatAsCurrency(totalValue) : '-'}
            </PDSText>
          </PDSBox>

          <PDSBox flexBasis="4rem">
            <PDSText color={textColor} weight="medium">
              {isFinite(percentageOfPortfolio)
                ? `${percentageOfPortfolio.toFixed(2)}%`
                : '-'}
            </PDSText>
          </PDSBox>

          <PDSBox flexBasis="4rem">
            <PDSText color={textColor} weight="medium">
              {isFinite(gainLoss) ? formatAsCurrency(gainLoss) : '-'}
            </PDSText>
          </PDSBox>

          <PDSFlexBox
            flexBasis="5rem"
            justifyContent={dayGain ? 'flex-end' : 'center'}
          >
            {dayGain ? (
              <PDSValueTag
                isNeutral={!isSupported}
                minWidth="4.5rem"
                size="sm"
                value={dayGain}
                variant="arrow"
              />
            ) : (
              <PDSText color={textColor} weight="medium">
                -
              </PDSText>
            )}
          </PDSFlexBox>
        </PDSFlexBox>
      </PDSSubCard>
    </PDSLinkWrapper>
  )
}
